<script setup>
import _ from 'lodash'
import { ref, watch, computed, getCurrentInstance, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { axios } from '@/utils/axios'
import { useBackgroundActions } from '@/composables/useBackgroundActions'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
    Select as SelectRoot,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useMeta } from 'vue-meta'
import { ContentLayout } from '@/components/ui/layouts'
import ReportFilters from '@/partials/options/ReportFilters.vue'
import ReportTable from '@/partials/tables/ReportTable.vue'
import CreatorAggregationChart from '@/partials/charts/CreatorAggregationChart.vue'
import ContentTable from '@/partials/tables/ContentTable.vue'
import SkeletonReports from '@/partials/skeletons/SkeletonReports.vue'
import SkeletonReportItem from '@/partials/skeletons/SkeletonReportItem.vue'
import DiscoveryPagination from '@/partials/tables/discovery/DiscoveryPagination.vue'
import Breadcrumb from '@/partials/navigation/Breadcrumb.vue'
import { Dialog as UIDialog, DialogHeader, DialogTitle, DialogDescription, DialogScrollContent, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { NoResults } from '@/components/ui/empty-state'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Input } from '@/components/ui/input'
import { Heading } from '@/components/ui/typography'
import Tooltip from '@/components/Tooltip.vue'

import EnterTextModal from '@/partials/modals/Generic/EnterTextModal.vue'

import { trackEvent, AnalyticalEvents } from '@/utils/analytics'

const reportScope = ref('user') // 'user' or 'public'
const activeTab = ref('overview')
const activeSubPage = ref('history') // 'history', 'generating', or 'view'

const route = useRoute()
const router = useRouter()
const store = useStore()
const { getCountryFromCode, getLanguageFromCode } = store.getters
const instance = getCurrentInstance()
const {
    addBackgroundAction,
    updateBackgroundAction,
    startPolling,
    stopPolling,
    completeBackgroundAction,
    failBackgroundAction,
    showBackgroundActionToast,
} = useBackgroundActions()

const creatorsComponent = ref(null)
const overviewComponent = ref(null)

const report = ref(null)
const creators = ref([])
const reportItemFilterOptions = ref({ languages: [], countries: [] })

const isLoadingReport = ref(false)
const isLoadingItems = ref(false)
const isLoadingFacets = ref(false)
const isLoadingHistory = ref(true)
const isLoadingListHistory = ref(false)
const isGenerateOpen = ref(false)
const isRenameOpen = ref(false)
const isCopyExisting = ref(false)
const isEditExisting = ref(false)
const isRegenExisting = ref(false)
const isDeleteOpen = ref(false)
const isReportActionsMenuOpen = ref(false)
const isPdfGenerating = ref(false)
const isRefreshingAggregations = ref(false)
const isGeneratingNewReport = ref(false) // Flag to prevent route watcher from interfering

const entityTotals = ref({})
const hasPendingSponsorshipOverrides = ref(false)

// Background job polling state
const reportProgress = ref(0)
const progressMessage = ref('')
const pollingInterval = ref(null)
const currentBackgroundActionId = ref(null)
const lastReportParams = ref(null) // Store params for retry

const renameReportDefault = ref('')

const defaultSplitOption = Object.freeze({
    value: 'all',
    dimension: 'all',
    key: 'all',
    label: 'All Content',
})
const splitSelectionValue = ref(defaultSplitOption.value)

const getPlatformDisplayName = (platformId) => {
    if (platformId === 'youtube') return 'YouTube'
    if (platformId === 'tiktok') return 'TikTok'
    if (platformId === 'twitter') return 'X'
    return _.startCase(platformId)
}

const isLoading = computed(() => { return isLoadingReport.value || isLoadingItems.value })
const isLoadingAnyHistory = computed(() => { return isLoadingHistory.value || isLoadingListHistory.value })
const isSponsorshipDetectionEnabled = computed(() => {
    const sponsorshipConfig = report.value
        ? report.value.options?.sponsorshipConfig
        : filters.value?.sponsorshipConfig

    if (!sponsorshipConfig) {
        return true
    }

    return sponsorshipConfig.enabled === true
})

const organicTimeseriesData = computed(() => {
    if (!report.value?.facets) {
        return null
    }

    return isSponsorshipDetectionEnabled.value ?
        report.value.facets.by_sponsored?.false?.timeseries :
        report.value.facets.all?.all?.timeseries
})

const sponsoredTimeseriesData = computed(() => {
    return report.value?.facets?.by_sponsored?.true?.timeseries
})

const splitDropdownGroups = computed(() => {
    const facets = report.value?.facets
    if (!facets) return []

    const groups = []

    const platformFacet = facets.by_platform
    if (platformFacet && Object.keys(platformFacet).length > 0) {
        groups.push({
            label: 'By Platform',
            options: Object.keys(platformFacet)
                .sort()
                .map((platformId) => ({
                    value: `by_platform:${platformId}`,
                    dimension: 'by_platform',
                    key: platformId,
                    label: getPlatformDisplayName(platformId),
                })),
        })
    }

    const contentFacet = facets.by_content_type
    if (contentFacet && Object.keys(contentFacet).length > 0) {
        groups.push({
            label: 'By Content Type',
            options: Object.keys(contentFacet)
                .sort()
                .map((contentType) => ({
                    value: `by_content_type:${contentType}`,
                    dimension: 'by_content_type',
                    key: contentType,
                    label: _.startCase(contentType.replace(/_/g, ' ')),
                })),
        })
    }

    const sponsoredFacet = facets.by_sponsored
    if (sponsoredFacet && (sponsoredFacet.true || sponsoredFacet.false)) {
        const options = []
        if (sponsoredFacet.false) {
            options.push({
                value: 'by_sponsored:false',
                dimension: 'by_sponsored',
                key: 'false',
                label: 'Organic',
            })
        }
        if (sponsoredFacet.true) {
            options.push({
                value: 'by_sponsored:true',
                dimension: 'by_sponsored',
                key: 'true',
                label: 'Sponsored',
            })
        }

        if (options.length > 0) {
            groups.push({
                label: 'Sponsored Status',
                options,
            })
        }
    }

    return groups
})

const splitOptionsIndex = computed(() => {
    const indexMap = {
        [defaultSplitOption.value]: defaultSplitOption,
    }

    splitDropdownGroups.value.forEach((group) => {
        group.options.forEach((option) => {
            indexMap[option.value] = option
        })
    })

    return indexMap
})

const selectedSplitOptionDetails = computed(() => {
    return splitOptionsIndex.value[splitSelectionValue.value] || defaultSplitOption
})

const selectedFacetDimension = computed(() => selectedSplitOptionDetails.value.dimension)
const selectedFacetKey = computed(() => selectedSplitOptionDetails.value.key)
const selectedFacetLabel = computed(() => selectedSplitOptionDetails.value.label)

watch(
    () => [report.value?._id, splitOptionsIndex.value],
    () => {
        if (!splitOptionsIndex.value[splitSelectionValue.value]) {
            splitSelectionValue.value = defaultSplitOption.value
        }
    },
)

const currentAggregates = computed(() => {
    if (!report.value?.facets) return {}
    if (selectedFacetDimension.value === 'all') {
        return report.value.facets.all?.all || {}
    }

    return _.get(report.value.facets, [selectedFacetDimension.value, selectedFacetKey.value]) ||
        report.value.facets.all?.all ||
        {}
})

const selectedFacetNode = computed(() => {
    if (!report.value?.facets) return null
    if (selectedFacetDimension.value === 'all') {
        return report.value.facets.all?.all || null
    }
    return _.get(report.value.facets, [selectedFacetDimension.value, selectedFacetKey.value]) || null
})

const demographicFacets = computed(() => {
    if (!report.value?.facets) return null
    const subsetFacets = selectedFacetNode.value?.facets
    if (subsetFacets && Object.keys(subsetFacets).length > 0) {
        return {
            ...report.value.facets,
            ...subsetFacets,
        }
    }
    return report.value.facets
})
const isDevModeEnabled = computed(() => store.getters.isDevModeEnabled)
const orgDefaultPlatformId = computed(() => store.getters.selfDefaultPlatformId)
const platformId = ref(orgDefaultPlatformId.value)

const reportPlatforms =  ['instagram', 'tiktok', 'youtube', 'twitch', 'twitter']
const reportScopes = ['user', 'public']

const defaultPaging = {
    perPage: 10,
    currentPage: 1,
    totalPages: 1,
    sort: [
        {
            field: 'summary.sum_content',
            direction: 'desc',
            source: 'default',
        },
    ],
    filters: [],
    totalRecords: 0,
}
const paging = ref(_.cloneDeep(defaultPaging))
const filters = ref({})

const selectedPlatformIds = ref([orgDefaultPlatformId.value])

const reportPlatformIds = computed(() => {
    // Get platform IDs from report options if available
    if (report.value?.options?.platformIds && Array.isArray(report.value.options.platformIds)) {
        return report.value.options.platformIds
    }
    // Fallback to single platformId for backward compatibility with old reports
    if (report.value?.options?.platformId) {
        return [report.value.options.platformId]
    }
    // Only use default if selectedPlatformIds is truly empty (not just initialized)
    // This prevents auto-adding default platform when user explicitly clears selection
    return selectedPlatformIds.value.length > 0 ? selectedPlatformIds.value : [orgDefaultPlatformId.value]
})

// Available filter options for tables (derived from report facets)
const availablePlatformOptions = computed(() => {
    const platformFacet = report.value?.facets?.by_platform
    if (!platformFacet) return []

    return Object.keys(platformFacet)
        .filter(key => !key.startsWith('_'))
        .sort()
        .map((platformId) => ({
            value: platformId,
            label: getPlatformDisplayName(platformId),
        }))
})

const availableContentTypeOptions = computed(() => {
    const contentFacet = report.value?.facets?.by_content_type
    if (!contentFacet) return []

    return Object.keys(contentFacet)
        .filter(key => !key.startsWith('_'))
        .sort()
        .map((contentType) => ({
            value: contentType,
            label: _.startCase(contentType.replace(/_/g, ' ')),
        }))
})


const availableLanguageOptions = computed(() =>
    reportItemFilterOptions.value.languages
        .map(code => ({ value: code, label: getLanguageFromCode(code) }))
        .sort((a, b) => a.label.localeCompare(b.label))
)

const availableCountryOptions = computed(() =>
    reportItemFilterOptions.value.countries
        .map(code => ({ value: code, label: getCountryFromCode(code) }))
        .sort((a, b) => a.label.localeCompare(b.label))
)

const reportHistory = ref([])
const reportHistoryText = ref('')
const reportHistoryFilter = ref({ platform_id: orgDefaultPlatformId.value, is_public: false })
const reportHistoryPaging = ref({
    perPage: 20,
    currentPage: 1,
    totalPages: 1,
    sort: {
        sortby: 'generated_at',
        sortdir: 'desc',
    },
    filters: [],
    totalRecords: 0,
})

const insufficientQuota = computed(() => {
    return !store.getters.isLegacyBilling && ( store.getters.quotaRemaining({ quotaType : 'reportGeneration'}) <= 0 )
})

watch(selectedPlatformIds, () => {
    filters.value.game = null
})

watch(reportHistoryText, () => {
    if (reportHistoryText.value && activeSubPage.value !== 'history') {
        isLoadingHistory.value = true
        activeSubPage.value = 'history'
    }
    deBounceGetReportHistory()
})

watch(
  () => route.query.report_id,
  (newVal, oldVal) => {
    if (!newVal && activeSubPage.value === 'view') {
      // User navigated from /reports?report_id= to /reports
      report.value = null
      creators.value = []
      activeSubPage.value = 'history'
      getReportHistory()
    } else if (newVal && newVal !== oldVal && !isGeneratingNewReport.value) {
      // Trigger report viewed event
      trackEvent(AnalyticalEvents.ReportViewed, {
        report_id: newVal,
      })

      // If report_id changed (and we're not generating a new report), load the report
      // The isGeneratingNewReport flag prevents this watcher from calling showExistingReport
      // when we're in the middle of generating a new report
      showExistingReport()
    }
  }
)

// Show the background action toast when user leaves the generating page while job is still running
watch(
  activeSubPage,
  (newVal, oldVal) => {
    if (oldVal === 'generating' && newVal !== 'generating' && currentBackgroundActionId.value) {
      // Only show toast if the job is still in-progress
      // (If it completed, user is being auto-redirected to view - no need for toast)
      const action = store.getters.getBackgroundActionById(currentBackgroundActionId.value)
      if (action && action.status === 'in-progress') {
        showBackgroundActionToast(currentBackgroundActionId.value)
      }
    }
  }
)

useMeta({
    title: 'Reports',
})

const getReportById = async () => {
    isLoadingReport.value = true
    try {
        const response = await axios.get(`/reports/${route.query.report_id}`)
        const reportData = response.data.payload
        report.value = reportData

        if(!['pending', 'processing'].includes(reportData?.status)) {
            // Set reportHistoryFilter to first platform for history filtering
            const reportPlatforms = reportData.options.platformIds || (reportData.options.platformId ? [reportData.options.platformId] : [])
            reportHistoryFilter.value.platform_id = reportPlatforms[0] || orgDefaultPlatformId.value
            getReportFacets()
        }

    } catch (error) {
        console.error('Error fetching report items:', error)
    } finally {

        isLoadingReport.value = false

        if (!report.value) {
            activeSubPage.value = 'history'
        }
        else if(report?.value?.status === 'failed') {
            if (report?.value?.status_reason === 'size') {
                activeSubPage.value = 'error-size'
            }
            else {
                activeSubPage.value = 'error'
            }

        }
        else if(['pending', 'processing'].includes(report?.value?.status)) {
            activeSubPage.value = 'generating'
            startProgressPolling(report.value._id, report.value?.options?.title, report.value.progress, report.value.progress_message)
        }
        else {
            activeSubPage.value = 'view'
        }

    }

    if (route.query?.showcopy === 'true') {
        const { showcopy, ...remainingQuery } = route.query
        await router.replace({ query: remainingQuery })
        onShowGenerateReport(true)
    }
}


/**
 * Build report options from current filters
 * Extracted to enable retry functionality
 */
function buildReportOptions() {
    // Determine platform IDs - always use array
    const platformIds = filters.value.platformIds || (filters.value.platformId ? [filters.value.platformId] : [])
    const isLinksOnlyReport = filters.value.additionalContentOnly === true

    let gameName = null
    const gameIds = []

    if (filters.value.game) {

        gameName = filters.value.game.label

        gameIds.push({ platform_id: 'igdb', game_id: filters.value.game.igdb_id })

        if (filters.value.game?._platforms) {
            const platforms = filters.value.game._platforms
            if (platforms.twitch?.id && typeof platforms.twitch.id === 'string' && platformIds.includes('twitch')) {
                gameIds.push({ platform_id: 'twitch', game_id: platforms.twitch.id })
            }
            // Check for manual YouTube channel ID first, then platform linking data
            if (platformIds.includes('youtube')) {
                if (filters.value.manualYouTubeChannelId) {
                    gameIds.push({ platform_id: 'youtube', game_id: filters.value.manualYouTubeChannelId })
                } else if (platforms.youtube?.id && typeof platforms.youtube.id === 'string') {
                    gameIds.push({ platform_id: 'youtube', game_id: platforms.youtube.id })
                }
            }
        }

        // Fallback: if no _platforms but we have a game ID, use it for the selected platform
        if (gameIds.length === 0 && filters.value.game?.id) {
            platformIds.forEach(singlePlatform => {
                if (['twitch', 'youtube'].includes(singlePlatform)) {
                    const hasGameIdForPlatform = gameIds.some(g => g.platform_id === singlePlatform)
                    if (!hasGameIdForPlatform) {
                        if (singlePlatform === 'youtube' && filters.value.manualYouTubeChannelId) {
                            gameIds.push({ platform_id: 'youtube', game_id: filters.value.manualYouTubeChannelId })
                        } else {
                            gameIds.push({ platform_id: singlePlatform, game_id: filters.value.game.id })
                        }
                    }
                }
            })
        }

    }


    // hashtags: [{ platform_id: 'twitter', hashtag: '...' }, ...]
    const hashtags = []
    const hashtagSupportedPlatforms = ['twitter', 'instagram', 'tiktok', 'youtube', 'twitch']
    const userHashtags = _.map(filters.value.tags, 'id')
    const gameHashtags = filters.value.gameHashtagsForNonGamePlatforms || []
    const nonGamePlatforms = ['twitter', 'instagram', 'tiktok']

    userHashtags.forEach(hashtag => {
        platformIds.forEach(platformId => {
            if (hashtagSupportedPlatforms.includes(platformId)) {
                hashtags.push({ platform_id: platformId, hashtag })
            }
        })
    })

    gameHashtags.forEach(hashtag => {
        platformIds.forEach(platformId => {
            if (nonGamePlatforms.includes(platformId)) {
                hashtags.push({ platform_id: platformId, hashtag })
            }
        })
    })

    // mentions: [{ platform_id: 'instagram', mention: '...' }]
    const mentions = []
    const userMentions = filters.value.taggedUserNames || []
    userMentions.forEach(mention => {
        if (platformIds.includes('instagram')) {
            mentions.push({ platform_id: 'instagram', mention })
        }
    })

    let options = {
        platformIds: platformIds,
        title: filters.value.title,
        sponsorshipConfig : filters.value.sponsorshipConfig
    }

    options.additionalContent = filters.value.additionalContent

    if (!isLinksOnlyReport) {
        options.contentType = filters.value.contentType || null
    }

    if (isLinksOnlyReport) {
        options.additionalContentOnly = true
    }
    else {
        options.game_ids = gameIds.length > 0 ? gameIds : null
        options.hashtags = hashtags.length > 0 ? hashtags : null
        options.mentions = mentions.length > 0 ? mentions : null
        options.hashtagGameFilterLogic = filters.value.hashtagGameFilterLogic || 'and'
        options.gameName = gameName
        options.startAt = filters.value.startAt
        options.endAt = filters.value.endAt
        options.isIgnoreTiny = (platformIds.includes('twitch') && !filters.value.listId && filters.value.isIgnoreTiny)
        options.listId = filters.value.listId
    }

    return {
        report_type: 'platform-creator-report',
        options,
        isExport: false,
    }
}

/**
 * Retry generating a report with stored parameters
 */
async function retryReportGeneration(savedParams) {
    if (savedParams) {
        // Restore the filters from saved params
        lastReportParams.value = savedParams
    }
    await generateReportWithParams(lastReportParams.value)
}

/**
 * Generate report with specific params (used for initial generation and retry)
 */
async function generateReportWithParams(reportParams, editReportId) {
    isLoadingReport.value = true
    isGeneratingNewReport.value = true
    isGenerateOpen.value = false

    store.dispatch('CANCEL_PENDING_REQUESTS_BY_REGEX', '^/reports')

    try {

        let response = null

        if (editReportId) {
            response = await axios.patch(`/reports/${editReportId}`, {
                action : 'edit',
                value : reportParams
            })
        }
        else {
            response = await axios.post('/reports', reportParams)
        }

        const reportData = response.data.payload

        // Track report created event
        trackEvent(AnalyticalEvents.ReportCreated, {
            report_id: reportData._id,
        })

        if (reportData.status === 'pending') {
            // Show the generating state BEFORE changing the route
            activeSubPage.value = 'generating'

            // Now update the route (after setting activeSubPage)
            await router.push({
                query: {
                    ...route.query,
                    report_id: reportData._id,
                },
            })

            startProgressPolling(reportData._id, reportParams?.options?.title || 'Report')
        } else {
            // Synchronous completion (fallback mode without queue)
            report.value = reportData
            isLoadingReport.value = false
            // Transition directly to view since it's complete
            activeSubPage.value = 'view'

            // Now update the route
            await router.push({
                query: {
                    ...route.query,
                    report_id: reportData._id,
                },
            })

            isGeneratingNewReport.value = false

            getReportItems()
            getReportFacets()
            getReportHistory()
        }


    } catch (error) {
        isGeneratingNewReport.value = false
        isLoadingReport.value = false
        reportProgress.value = 0
        progressMessage.value = ''
        activeSubPage.value = 'error'
    }
}

async function startProgressPolling(id, title, initalProgress, initalProcessMessage) {
    // Clear any existing local polling
    reportProgress.value = 0
    progressMessage.value = 'Queuing report...'

    if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
        pollingInterval.value = null
    }

    const reportId = id
    let backgroundActionId = null
    try {
        // Now that we have the report ID, create the background action with cancelUrl
        backgroundActionId = await addBackgroundAction({
            description: `Generating: ${title}`,
            progress: 0,
            progressMessage: 'Queuing report...',
            cancelUrl: `/reports/${reportId}/cancel`,
            suppressToast: true,
        })
        currentBackgroundActionId.value = backgroundActionId

        reportProgress.value = initalProgress || 0
        progressMessage.value = initalProcessMessage || 'Queued'

        startPolling(backgroundActionId, `/reports/${reportId}/status`, {
            onComplete: async (statusData) => {
                // Fetch the complete report if we're still on this report
                if (route.query.report_id === reportId) {
                    try {
                        const reportResponse = await axios.get(`/reports/${reportId}`)
                        report.value = reportResponse.data.payload
                        isLoadingReport.value = false
                        reportProgress.value = 100
                        progressMessage.value = 'Complete'
                        activeSubPage.value = 'view'
                        getReportItems()
                        getReportFacets()
                        getReportHistory()
                    } catch (e) {
                        console.error('Error fetching completed report:', e)
                    }
                }

                completeBackgroundAction(backgroundActionId, {
                    resultId: reportId,
                    navigateTo: { name: 'reports', query: { report_id: reportId } },
                })

                currentBackgroundActionId.value = null
                isGeneratingNewReport.value = false
            },
            onFail: (error, statusData) => {

                if (statusData?.status_reason === 'size') {

                    failBackgroundAction(backgroundActionId, 'Unfortunately the report you attempted to generate was too large. Please try again with a more restricted date range.', {
                        resultId: reportId,
                        navigateTo: { name: 'reports', query: { report_id: reportId, showcopy: true } },
                    })
                    activeSubPage.value = 'error-size'
                    //Only load the report if the user is still on the generated report's page
                    if (route.query.report_id === reportId) {
                        getReportById()
                    }

                    return
                }

                isLoadingReport.value = false
                reportProgress.value = 0
                progressMessage.value = ''
                // Go back to history on failure
                activeSubPage.value = 'error'

                failBackgroundAction(backgroundActionId, error)
                currentBackgroundActionId.value = null
                isGeneratingNewReport.value = false
            },
        })

        pollingInterval.value = setInterval(async () => {
            try {
                const statusResponse = await axios.get(`/reports/${reportId}/status`)
                const statusData = statusResponse.data.payload
                reportProgress.value = statusData.progress || 0
                progressMessage.value = statusData.progress_message || 'Processing...'

                if (statusData.status === 'completed' || statusData.status === 'failed') {
                    if (pollingInterval.value) {
                        clearInterval(pollingInterval.value)
                        pollingInterval.value = null
                    }
                }
            } catch (e) {}
        }, 2000)
    } catch (error) {
        // Don't log cancellation errors as they're expected
        if (!error.is_cancelled) {
            console.error('Error generating report:', error)

            // Get error message
            const errorMessage = error?.response?.data?.message ||
                                error?.message ||
                                'Failed to generate report. Please try again.'

            // Fail the background action - it will show retry button (if it was created)
            if (backgroundActionId) {
                failBackgroundAction(backgroundActionId, errorMessage)
            }
            currentBackgroundActionId.value = null
        }

        isLoadingReport.value = false
        reportProgress.value = 0
        progressMessage.value = ''
        activeSubPage.value = 'error'
    }
}

async function generateReport(editReportId) {
    // Build and store params for potential retry
    const reportParams = buildReportOptions()
    lastReportParams.value = reportParams

    await generateReportWithParams(reportParams, editReportId)
}

const getReportItems = async () => {
    isLoadingItems.value = true
    store.dispatch('CANCEL_PENDING_REQUESTS_BY_REGEX', '^/report/[^/]+$/items')

    try {
        // Don't reset filters - preserve platform filters and other filters
        if (!paging.value.filters) {
            paging.value.filters = []
        }

        // Build request payload
        const requestPayload = {
            paging: paging.value,
            isExport: false,
        }

        const response = await axios.post(`/reports/${route.query.report_id}/items`, requestPayload)
        const reportData = response.data.payload
        creators.value = reportData.items

        if (reportData.filterOptions) {
            reportItemFilterOptions.value = reportData.filterOptions
        }

        // Preserve user-applied filters and sort when merging server paging response
        const currentFilters = paging.value.filters || []
        const currentSort = paging.value.sort || []
        paging.value = { ...reportData.paging, filters: currentFilters, sort: currentSort }
    } catch (error) {
        console.error('Error fetching report items:', error)
    } finally {
        isLoadingItems.value = false
    }
}

const getReportFacets = async () => {
    store.dispatch('CANCEL_PENDING_REQUESTS_BY_REGEX', '^/report/[^/]+$/facets')

    try {
        isLoadingFacets.value = true
        const requestPayload = {}

        const response = await axios.post(`/reports/${route.query.report_id}/facets`, requestPayload)
        const facets = response.data?.payload?.facets

        if (facets && report.value.facets) {
            _.forEach(facets, facet => {
                report.value.facets[facet.key] = facet.value
            })
        }

    } catch (error) {
        console.error('Error fetching report facets:', error)
    }
    isLoadingFacets.value = false
}

async function exportReportItems() {
    try {
        const exportPaging = {
            ...paging.value,
        }

        exportPaging.perPage = 10_000
        exportPaging.currentPage = 1
        exportPaging.filters = []

        const performExport = async () => {
            return await axios.post(`/reports/${route.query.report_id}/items`, {
                paging: exportPaging,
                isExport: true,
        })}

        // Trigger report export event
        if (route?.query?.report_id) {
            trackEvent(AnalyticalEvents.ReportExported, {
                report_id: route.query.report_id,
            })
        }

        creatorsComponent.value?.onExportItems(performExport, null)
    } catch (error) {
        console.error('Error fetching report:', error)
        // Handle error (e.g., show notification)
    }
}

const getReportHistory = async () => {
    isLoadingHistory.value = true

    try {
        const historyQuery = {
            report_type: 'platform-creator-report',
            // TODO: Uncomment when public reports are available
            // is_public: reportScope.value === 'public',
            paging: _.cloneDeep(reportHistoryPaging.value),
        }

        // TODO: Remove when public reports are available
        if (isDevModeEnabled.value) {
            historyQuery.is_public = reportScope.value === 'public'
        }

        if (reportHistoryFilter.value.platform_id && reportHistoryFilter.value.platform_id !== 'all') {
            historyQuery.paging.filters.push({ id: 'platform_id', value: reportHistoryFilter.value.platform_id })
        }

        if (reportHistoryText.value) {
            historyQuery.paging.filters.push({ id: 'title', value: reportHistoryText.value })
        }

        const requestUrl = '/reports/history'
        store.dispatch('CANCEL_PENDING_REQUESTS_BY_ENDPOINT', requestUrl)

        const response = await axios.post(requestUrl, historyQuery)

        const reportData = response.data.payload
        reportHistory.value = reportData.reports
        entityTotals.value = reportData.entityTotals
        reportHistoryPaging.value.totalRecords = reportData.pager.totalRecords
        reportHistoryPaging.value.totalPages = Math.ceil(
            reportHistoryPaging.value.totalRecords / reportHistoryPaging.value.perPage,
        )
    } catch (error) {
        console.error('Error fetching report history:', error)
    } finally {
        isLoadingHistory.value = false
    }
}

const initializeForList = async (listId) => {
    isLoadingListHistory.value = true

    try {
        const historyQuery = {
            report_type: 'platform-creator-report',
            // TODO: Uncomment when public reports are available
            // is_public: reportScope.value === 'public',
            'paging' : {
                perPage: 1,
                currentPage: 1,
                totalPages: 1,
                sort: {
                    'sortby' : 'generated_at',
                    'sortdir' : 'desc',
                },
                filters: [],
                totalRecords: 0,
            }
        }

        // TODO: Remove once public reports are available
        if (isDevModeEnabled.value) {
            historyQuery.is_public = reportScope.value === 'public'
        }

        historyQuery.paging.filters.push(
            { id: 'platform_id', value: reportHistoryFilter.value.platform_id },
            { id: 'list_id', value: listId },
        )

        const requestUrl = '/reports/history'

        const response = await axios.post(requestUrl, historyQuery)
        const reportData = response.data.payload

        if (reportData.reports && reportData.reports.length > 0) {
            await router.push({
                query: {
                    ...route.query,
                    report_id: reportData.reports[0]._id
                }
            })

            // Trigger report viewed event
            if (route?.query?.report_id) {
                trackEvent(AnalyticalEvents.ReportViewed, {
                    report_id: route.query.report_id,
                })
            }


            showExistingReport()
        }
        else {
            onShowGenerateReport()
        }

    } catch (error) {
        console.error('Error fetching report history for list:', error)
    } finally {
        isLoadingListHistory.value = false
    }
}

const deBounceGetReportHistory = _.debounce(() => {
    getReportHistory()
}, 250)

const showExistingReport = async () => {
    reportItemFilterOptions.value = { languages: [], countries: [] }
    paging.value = _.cloneDeep(defaultPaging)
    activeTab.value = 'overview'
    activeSubPage.value = 'view'
    getReportById()
    getReportItems()
}

const hasReportOnAnyPlatform = computed(() => {
    let hasReport = false
    _.forEach(reportPlatforms, (platform) => {
        if (entityTotals.value[platform] > 0) {
            hasReport = true
        }
    })
    return hasReport
})

async function onFilterChange(newFilters, editReportId) {
    report.value = null
    creators.value = []
    reportItemFilterOptions.value = { languages: [], countries: [] }
    filters.value = newFilters
    paging.value = _.cloneDeep(defaultPaging)
    await generateReport(editReportId)
}

async function onPagingChange(newPaging) {
    // Handle undefined or null newPaging
    if (!newPaging) {
        return
    }

    // Preserve existing filters only when newPaging doesn't include filters
    const existingFilters = paging.value.filters || []

    paging.value = { ...paging.value, ...newPaging }

    // When newPaging.filters is provided use it directly.
    // emitFilterChange() in ReportTable always sends the current filter state,
    if (!Array.isArray(newPaging.filters)) {
        // No filters in this update preserve existing
        paging.value.filters = existingFilters
    }

    await getReportItems()
}

const onSelectReport = async (report) => {
    await router.push({
        query: {
            ...route.query,
            report_id: report._id,
        },
    })

    showExistingReport()
}

const onHistoryPlatformClick = (newPlatformId) => {
    platformId.value = newPlatformId
    activeSubPage.value = 'history'
    reloadReportHistory(newPlatformId)
}

const reloadReportHistory = (newPlatformId) => {
    reportHistoryFilter.value.platform_id = newPlatformId
    reportHistoryPaging.value.currentPage = 1
    getReportHistory()
}

const onShowHistory = () => {
    activeSubPage.value = 'history'
    router.push({ query: null })
}

const onHistoryPagingChange = (updatedPaging) => {
    if (updatedPaging.perPage !== undefined) {
        reportHistoryPaging.value.perPage = updatedPaging.perPage
    }
    if (updatedPaging.currentPage !== undefined) {
        reportHistoryPaging.value.currentPage = updatedPaging.currentPage
    }
    if (updatedPaging.totalPages !== undefined) {
        reportHistoryPaging.value.totalPages = updatedPaging.totalPages
    }

    getReportHistory()
}

const onReportScopeClick = (scope) => {
    reportScope.value = scope
    reportHistoryPaging.value.currentPage = 1
    activeSubPage.value = 'history'
    getReportHistory()
}

const onShowEditReport = () => {
    isGenerateOpen.value = true
    isEditExisting.value = true
    isCopyExisting.value = false
    isRegenExisting.value = false
}

const onShowGenerateReport = (isCopy, isRegen = false) => {
    isGenerateOpen.value = true
    isCopyExisting.value = isCopy === true
    isRegenExisting.value = isRegen === true
    isEditExisting.value = false
}

const onCloseGenerateReport = () => {
    isGenerateOpen.value = false
    isRegenExisting.value = false
    isEditExisting.value = false
}

const cancelReportGeneration = async () => {
    // Cancel the pending request
    store.dispatch('CANCEL_PENDING_REQUESTS_BY_REGEX', '^/reports')

    // Clear local polling interval if active
    if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
        pollingInterval.value = null
    }

    // Try to cancel the report on the backend
    const reportId = route.query.report_id
    if (reportId) {
        try {
            await axios.post(`/reports/${reportId}/cancel`)
            console.log('Report generation cancelled on backend')
        } catch (e) {
            console.warn('Could not cancel report on backend:', e?.message)
        }
    }

    getReportHistory()

    // Stop store-managed polling and remove the background action
    if (currentBackgroundActionId.value) {
        stopPolling(currentBackgroundActionId.value)
        store.dispatch('removeBackgroundAction', currentBackgroundActionId.value)
        currentBackgroundActionId.value = null
    }

    // Reset loading state
    isLoadingReport.value = false
    reportProgress.value = 0
    progressMessage.value = ''

    // Go back to history
    activeSubPage.value = 'history'
    router.push({ name: 'reports' })

    // Optionally show a message or reset to history view
    if (!report.value) {
        activeSubPage.value = 'history'
    }
}

const onDeleteReportConfirm = async () => {
    isDeleteOpen.value = false
    const reportIdToDelete = route.query.report_id
    onShowHistory()

    if (!reportIdToDelete) {
        return
    }

    isLoadingHistory.value = true
    try {
        await axios.delete(`/reports/${route.query.report_id}`)
    } catch (error) {
        console.error('Unable to delete report:', error)
    } finally {
        isLoadingHistory.value = false
    }
    getReportHistory()
}

// Sponsorship override handlers
const onSponsorshipOverrideApplied = () => {
    hasPendingSponsorshipOverrides.value = true
}

const onRefreshAggregations = async () => {
    if (!report.value?._id || isRefreshingAggregations.value) return

    isRefreshingAggregations.value = true

    // Create a background action for visibility
    const reportTitle = report.value?.title || 'Report'
    const backgroundActionId = await addBackgroundAction({
        description: `Refreshing: ${reportTitle}`,
        progress: 0,
        progressMessage: 'Applying sponsorship changes...',
    })

    try {
        // Update progress to show work is happening
        updateBackgroundAction(backgroundActionId, {
            progress: 30,
            progressMessage: 'Re-computing metrics...',
        })

        await axios.post(`/reports/${report.value._id}/refresh-aggregations`)

        updateBackgroundAction(backgroundActionId, {
            progress: 70,
            progressMessage: 'Loading updated report...',
        })

        // Reload the report to get updated aggregations
        await showExistingReport()

        hasPendingSponsorshipOverrides.value = false

        // Complete the background action
        completeBackgroundAction(backgroundActionId, {
            message: 'Report statistics updated successfully',
        })
    } catch (error) {
        console.error('Error refreshing aggregations:', error)

        // Fail the background action
        failBackgroundAction(backgroundActionId, {
            error: 'Failed to refresh report statistics. Please try again.',
        })
    } finally {
        isRefreshingAggregations.value = false
    }
}

const pdfProgress = ref({ current: 0, total: 0 })
const showPdfProgress = ref(false)

const onDownloadPdf = async () => {
    if (isPdfGenerating.value || !overviewComponent.value) return
    isPdfGenerating.value = true
    showPdfProgress.value = true
    pdfProgress.value = { current: 0, total: 0 }

    try {
        await overviewComponent.value.downloadPdf(
            report.value?.title || 'report',
            (current, total) => {
                pdfProgress.value = { current, total }
            }
        )

        // Show success notification
        if (instance?.appContext?.config?.globalProperties?.$notify) {
            instance.appContext.config.globalProperties.$notify({
                type: 'success',
                title: 'PDF Generated',
                text: 'Your report has been downloaded successfully.',
                duration: 3000,
            })
        }
    } catch (error) {
        console.error('Error downloading PDF:', error)

        if (instance?.appContext?.config?.globalProperties?.$notify) {
            instance.appContext.config.globalProperties.$notify({
                type: 'error',
                title: 'PDF Generation Failed',
                text: 'Failed to generate PDF report. Please try again.',
                duration: 5000,
            })
        }
    } finally {
        isPdfGenerating.value = false
        showPdfProgress.value = false
    }
}

if (route.query.report_id) {
    // Trigger report viewed event
    trackEvent(AnalyticalEvents.ReportViewed, {
        report_id: route.query.report_id,
    })

    showExistingReport()
} else if (route.query.platform_id) {
    platformId.value = route.query.platform_id
    selectedPlatformIds.value = [route.query.platform_id]
    reportHistoryFilter.value.platform_id = route.query.platform_id
}

if (route.query.list_id) {
    getReportHistory()
    initializeForList(route.query.list_id)
}
else {
    getReportHistory()
}

const headingTitle = computed(() => {
    switch (activeSubPage.value) {
        case 'view':
            return (report.value?.title || 'Report Details')
        case 'generating':
            return 'Generating Report'
        case 'error', 'error-size':
            return 'Unable to Generate Report'
    }
    return 'Reports'
})

const headingTitleClass = computed(() => {
    if (['view', 'generating', 'error', 'error-size'].includes(activeSubPage.value))
    {
        return 'text-xl font-semibold w-full'
    }
    return null
})

const activeBreadcrumbs = computed(() => {
    // Done this way so the report title populates the breadcrumb
    if (activeSubPage.value === 'view') {
        return [
            {
                label: 'Reports',
                href: { name: 'reports' },
                onClick: () => {
                    activeSubPage.value = 'history'
                },
            },
            {
                label: report.value?.title || 'Report Details',
            },
        ]
    }
    if (['generating', 'error', 'error-size'].includes(activeSubPage.value)) {
        return [
            {
                label: 'Reports',
                href: { name: 'reports' },
                onClick: () => {
                    activeSubPage.value = 'history'
                },
            },
            {
                label: 'Generating Report...',
            },
        ]
    }

    return []
})

// Clean up polling interval on unmount
onUnmounted(() => {
    if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
        pollingInterval.value = null
    }

    // If user navigates away while on the generating page with a job running, show the toast
    if (activeSubPage.value === 'generating' && currentBackgroundActionId.value) {
        const action = store.getters.getBackgroundActionById(currentBackgroundActionId.value)
        if (action && action.status === 'in-progress') {
            showBackgroundActionToast(currentBackgroundActionId.value)
        }
    }
})

// Helper functions for report cards
const formatDateRange = (startAt, endAt) => {
    if (!startAt || !endAt) return null
    const start = instance.appContext.config.globalProperties.$moment(startAt)
    const end = instance.appContext.config.globalProperties.$moment(endAt)
    const sameYear = start.year() === end.year()
    const sameMonth = start.month() === end.month()

    if (sameYear && sameMonth) {
        return `${start.format('MMM D')} - ${end.format('D, YYYY')}`
    } else if (sameYear) {
        return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
    }
    return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`
}

const formatLargeNumber = (num) => {
    if (num === null || num === undefined) return '0'
    if (num >= 1_000_000_000) {
        return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + ' billion'
    }
    if (num >= 1_000_000) {
        return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + ' million'
    }
    return num.toLocaleString()
}

const getPlatformBadgeClasses = (platformId) => {
    return 'bg-slate-100 text-slate-700 border-slate-200'
}

const getPlatformIconColor = (platformId) => {
    const colorMap = {
        twitch: 'text-purple-600',
        youtube: 'text-red-600',
        tiktok: 'text-slate-900',
        twitter: 'text-x',
        instagram: 'text-pink-600',
    }
    return colorMap[platformId] || 'text-slate-600'
}

const getReportPlatformIds = (item) => {
    if (!item.entity_id) return []
    if (item.entity_id.includes(',')) {
        return item.entity_id.split(',').map(p => p.trim())
    }
    return [item.entity_id]
}

const getReportHashtags = (item) => {
    if (!item.options?.hashtags) return []
    // hashtags is an array of { platform_id, hashtag }
    const uniqueHashtags = [...new Set(item.options.hashtags.map(h => h.hashtag))]
    return uniqueHashtags.slice(0, 2) // Limit to 2 for display
}

const onContactUs = () => {
   instance.appContext.config.globalProperties.intercomModule.show()
}


const onShowRename = () => {
    if (activeSubPage.value !== 'view' || !report.value) {
        return
    }
    renameReportDefault.value = report.value?.title
    isRenameOpen.value = true
}

const onRenameSave = async (newTitle) => {
    try {
        report.value.title = newTitle

        await axios.patch(`/reports/${report.value._id}`, {
            value : {
                title : newTitle
            },
            action : 'rename'
        })

        instance.appContext.config.globalProperties.$notify({
            type: 'success',
            title: 'Report Rename',
            text: 'Your report has been renamed.',
            duration: 3000,
        })

        getReportHistory()

    } catch (ex) {
        instance.appContext.config.globalProperties.$notify({
            type: 'error',
            title: 'Report Rename Failed',
            text: 'Failed to rename your report. Please try again.',
            duration: 5000,
        })
    }
}

const onRenameClose = () => {
    isRenameOpen.value = false
}


</script>

<template>
    <UIDialog v-if="isGenerateOpen" :open="isGenerateOpen" @update:open="onCloseGenerateReport">
        <DialogScrollContent :class="'my-4 flex max-h-[90vh] max-w-4xl flex-col border-none p-0'">
            <DialogHeader class="flex-shrink-0 border-b border-slate-100 px-6 py-4">
                <DialogTitle v-if="isEditExisting">Edit Existing Report</DialogTitle>
                <DialogTitle v-else-if="isCopyExisting">Copy Existing Report</DialogTitle>
                <DialogTitle v-else>Generate New Report</DialogTitle>

                <DialogDescription v-if="isEditExisting">Please update your report parameters below. You're report will then be re-generated with the new options.</DialogDescription>
                <DialogDescription v-else-if="isCopyExisting">Please update any of the parameters below. The report will then be copied.</DialogDescription>
                <DialogDescription>Configure your report parameters below. Select platforms, filters, and a date range to generate a new report.</DialogDescription>

            </DialogHeader>
            <ReportFilters
                :is-loading="isLoading"
                :platform-ids="reportPlatformIds"
                :defaultListId="route.query.list_id"
                :defaultTitle="(isRegenExisting || isEditExisting) ? report?.title : ''"
                :defaultReportOptions="(isCopyExisting || isEditExisting) ? report?.options : null"
                :editReportId="(isEditExisting) ? report?._id : null"
                @filter-change="onFilterChange"
                @platform-change="(platformIdsArray) => {
                    selectedPlatformIds.value = Array.isArray(platformIdsArray) ? platformIdsArray : [platformIdsArray]
                }"
            />
        </DialogScrollContent>
    </UIDialog>

    <UIDialog :open="isDeleteOpen" @update:open="(val) => { isDeleteOpen = val }">
        <DialogContent class="max-w-md">
            <DialogHeader>
                <DialogTitle>Delete Report</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this report? This action cannot be undone.
                </DialogDescription>
            </DialogHeader>
            <DialogFooter>
                <Button variant="outline" @click="isDeleteOpen = false">
                    Cancel
                </Button>
                <Button variant="destructive" @click="onDeleteReportConfirm()">
                    Delete
                </Button>
            </DialogFooter>
        </DialogContent>
    </UIDialog>

    <EnterTextModal
        :is-open="isRenameOpen"
        :on-cancel="onRenameClose"
        :on-save="onRenameSave"
        :on-close="onRenameClose"
        :inital-text="renameReportDefault"
        title="Rename Report"
        description="Please enter the new name for the report."
    />

    <!-- PDF Generation Progress Indicator -->
    <Transition name="fade">
        <div v-if="showPdfProgress" class="fixed bottom-5 right-5 z-50 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-lg">
            <div class="flex items-center gap-3">
                <div class="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600"></div>
                <div class="flex flex-col">
                    <span class="text-sm font-medium text-slate-900">Generating PDF</span>
                    <span class="text-xs text-slate-500">
                        Processing section {{ pdfProgress.current }} of {{ pdfProgress.total }}
                    </span>
                </div>
            </div>
        </div>
    </Transition>

    <ContentLayout>
        <header class="mb-6 sm:flex sm:items-center sm:justify-between">

            <!-- Left: Title / Report Platforms -->
            <div class="mb-4 sm:mb-0">
                <Breadcrumb v-if="['view','generating', 'error', 'error-size'].includes(activeSubPage)" :items="activeBreadcrumbs" />
                <div class="flex">
                    <Heading
                        :level="1"
                        :title="headingTitle"
                        :titleClass="headingTitleClass"
                    />
                </div>
            </div>

            <!-- Right: Actions -->
            <div class="grid grid-flow-col justify-start gap-2 sm:auto-cols-max sm:justify-end">
                <!-- Refresh Aggregations Button (shows when sponsorship overrides are pending) -->
                <Button
                    v-if="activeSubPage === 'view' && hasPendingSponsorshipOverrides"
                    variant="outline"
                    :disabled="isRefreshingAggregations"
                    @click="onRefreshAggregations"
                >
                    <AppIcon
                        name="sync"
                        class="h-4 w-4"
                        :class="{ 'animate-spin': isRefreshingAggregations }"
                    />
                    <span class="ml-2">{{ isRefreshingAggregations ? 'Refreshing...' : 'Apply Changes' }}</span>
                </Button>

                <DropdownMenu
                    v-if="activeSubPage === 'view'"
                    :open="isReportActionsMenuOpen"
                    @update:open="(value) => (isReportActionsMenuOpen = value)"
                >
                    <DropdownMenuTrigger as-child>
                        <Button variant="outline">
                            <AppIcon name="ellipsis" class="h-4 w-4 text-slate-500" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" class="w-48">
                        <!-- TODO: Re-enable PDF export once fully working -->
                        <!-- <DropdownMenuItem
                            v-if="activeTab === 'overview'"
                            class="cursor-pointer gap-2"
                            @click="onDownloadPdf"
                            :disabled="isPdfGenerating"
                        >
                            <AppIcon name="file-pdf" class="h-4 w-4 text-slate-500" />
                            <span>{{ isPdfGenerating ? 'Generating...' : 'Download PDF' }}</span>
                        </DropdownMenuItem> -->
                        <DropdownMenuItem
                            class="cursor-pointer gap-2"
                            :disabled="isRefreshingAggregations"
                            @click="onRefreshAggregations"
                        >
                            <AppIcon
                                name="sync"
                                class="h-4 w-4 text-slate-500"
                                :class="{ 'animate-spin': isRefreshingAggregations }"
                            />
                            <span>{{ isRefreshingAggregations ? 'Refreshing...' : 'Refresh report' }}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem class="cursor-pointer gap-2" @click="onShowRename()">
                            <AppIcon name="input-text" class="h-4 w-4 text-slate-500" />
                            <span>Rename report</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem class="cursor-pointer gap-2"  @click="onShowEditReport()">
                            <AppIcon name="edit" class="h-4 w-4 text-slate-500" />
                            <span>Edit report</span>
                        </DropdownMenuItem>

                        <Tooltip :show-tooltip="insufficientQuota">
                            <template #trigger>
                                <DropdownMenuItem class="cursor-pointer gap-2" :disabled="insufficientQuota" @click="onShowGenerateReport(true)">
                                    <AppIcon name="copy" class="h-4 w-4 text-slate-500" />
                                    <span>Copy report</span>
                                </DropdownMenuItem>
                            </template>
                            <template #default>
                                <div class="whitespace-normal text-xs">Please ensure you have enough quota to generate a report.</div>
                            </template>
                        </Tooltip>

                        <DropdownMenuItem
                            class="cursor-pointer gap-2 text-rose-600 focus:text-rose-600"
                            @click="isDeleteOpen = true"
                        >
                            <AppIcon name="trash" class="h-4 w-4" />
                            <span>Delete report</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                <Tooltip v-if="activeSubPage === 'history'" :show-tooltip="insufficientQuota">
                    <template #trigger>
                        <Button
                            :disabled="insufficientQuota"
                            class="w-10 px-0 xl:w-auto xl:px-4"
                            @click="onShowGenerateReport(false)"
                        >
                            <AppIcon
                                name="plus"
                                class="h-[18px] w-[18px] xl:mr-2"
                                stroke-width="2.5"
                            />
                            <span class="hidden xl:block">Create Report</span>
                        </Button>
                    </template>
                    <template #default>
                        <div class="whitespace-normal text-xs">Please ensure you have enough quota to generate a report.</div>
                    </template>
                </Tooltip>

                <hr v-if="activeSubPage === 'history'" class="h-8 w-px bg-slate-200" />

                <!-- Search form -->
                <div
                    v-if="activeSubPage === 'history'"
                    class="relative"
                >
                    <AppIcon
                        name="magnifying-glass"
                        class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                    />
                    <Input
                        v-model="reportHistoryText"
                        type="search"
                        placeholder="Search reports..."
                        class="pl-9"
                    />
                </div>
            </div>
        </header>

        <div
            v-if="activeSubPage === 'history'"
            class="flex items-center gap-4 mb-6"
        >
            <div class="flex w-full flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <!-- Platform Filter Buttons -->
                <div
                    class="w-full overflow-x-auto sm:w-auto rounded p-1 mb-2 sm:mb-0 mt-2 sm:mt-0"
                    :class="{ 'bg-slate-200': hasReportOnAnyPlatform }"
                >
                    <div
                        v-if="hasReportOnAnyPlatform"
                        class="inline-flex justify-start min-w-max"
                        role="group"
                    >
                        <button
                            class="flex items-center rounded px-4 py-2.5 leading-tight"
                            :class="{
                                'bg-white': reportHistoryFilter.platform_id === 'all',
                            }"
                            @click.prevent="onHistoryPlatformClick('all')"
                        >
                            <span class="ml-2 whitespace-nowrap text-xs font-bold text-slate-500">
                               All ( {{ entityTotals['all'] ? entityTotals['all'] : 0 }} )
                            </span>
                        </button>
                        <button
                            v-for="item in reportPlatforms"
                            :key="item"
                            class="flex items-center rounded px-4 py-2.5 leading-tight"
                            :class="{
                                'bg-white': reportHistoryFilter.platform_id === item,
                            }"
                            @click.prevent="onHistoryPlatformClick(item)"
                        >
                            <AppIcon
                                class="h-4 w-4"
                                :name="item === 'twitter' ? 'x-twitter' : item"
                                :class="getPlatformIconColor(item)"
                            />
                            <span class="ml-2 whitespace-nowrap text-xs font-bold text-slate-500">
                                ( {{ entityTotals[item] ? entityTotals[item] : 0 }} )
                            </span>
                        </button>
                    </div>
                </div>

                <!-- Scope Filter Buttons -->
                <!-- TODO: Remove isDevModeEnabled when public reports are available -->
                <div
                    v-if="isDevModeEnabled"
                    class="w-full sm:w-auto inline-flex rounded bg-slate-200 p-1"
                >
                    <button
                        v-for="scope in reportScopes"
                        :key="scope"
                        class="flex grow rounded px-4 py-2.5 text-sm font-bold leading-tight text-slate-900 transition duration-150 ease-in-out focus:outline-none focus:ring-0"
                        :class="{ 'bg-white': reportScope === scope }"
                        @click="onReportScopeClick(scope)"
                    >
                        <span class="mx-auto">
                            {{ scope === 'user' ? 'My Reports' : 'Public Reports' }}
                        </span>
                    </button>
                </div>
            </div>
        </div>

        <template v-if="activeSubPage === 'history'">

            <div class="mt-2 mb-2">
                <SkeletonReports v-if="isLoadingAnyHistory" />

                <div v-else>
                    <div v-if="reportHistory.length > 0">
                        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                            <div
                                v-for="(item, index) in reportHistory"
                                :key="`${item.name}-${index}`"
                                class="group relative flex cursor-pointer flex-col rounded-lg border bg-white p-5 shadow-sm transition-all duration-200 overflow-hidden"
                                :class="
                                    item._id === report?._id
                                        ? 'border-slate-400 ring-2 ring-slate-200'
                                        : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                                "
                                @click="onSelectReport(item)"
                            >
                                <div class="flex flex-col h-full">
                                    <!-- Title -->
                                    <h3 class="text-base font-semibold text-slate-900 mb-2 line-clamp-2">
                                        {{ item.title }}
                                    </h3>

                                    <!-- Header: Platform badges + Date range -->
                                    <div class="flex items-start justify-between gap-2 mb-3">
                                        <!-- Platform badges -->
                                        <div class="flex flex-wrap gap-1.5">
                                            <span
                                                v-for="platformId in getReportPlatformIds(item)"
                                                :key="platformId"
                                                class="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium"
                                                :class="getPlatformBadgeClasses(platformId)"
                                            >
                                                <AppIcon
                                                    :name="platformId === 'twitter' ? 'x-twitter' : platformId"
                                                    class="h-3 w-3"
                                                    :class="getPlatformIconColor(platformId)"
                                                />
                                                <span :class="platformId === 'twitter' ? '' : 'capitalize'">{{ getPlatformDisplayName(platformId) }}</span>
                                            </span>
                                        </div>
                                        <!-- Date range -->
                                        <div
                                            v-if="formatDateRange(item.options?.startAt, item.options?.endAt)"
                                            class="shrink-0 inline-flex items-center text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-md border border-slate-200"
                                        >
                                            {{ formatDateRange(item.options?.startAt, item.options?.endAt) }}
                                        </div>
                                    </div>

                                    <!-- Game/Topic + Hashtags -->
                                    <div v-if="item.options?.gameName || getReportHashtags(item).length > 0" class="flex flex-wrap items-center gap-2 mb-3">
                                        <span
                                            v-if="item.options?.gameName"
                                            class="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 border border-emerald-200 px-2 py-1 rounded-md"
                                        >
                                            <AppIcon name="gamepad" class="h-2.5 w-2.5" />
                                            {{ item.options.gameName }}
                                        </span>
                                        <span
                                            v-for="hashtag in getReportHashtags(item)"
                                            :key="hashtag"
                                            class="inline-flex items-center gap-1 text-xs font-medium text-blue-700 border border-blue-200 px-2 py-1 rounded-md"
                                        >
                                            #{{ hashtag }}
                                        </span>
                                    </div>

                                    <!-- Stats row -->
                                    <div class="flex items-center gap-4 py-3 border-y border-slate-100 mb-3 mt-auto">
                                        <div class="flex flex-col">
                                            <span class="text-xs text-slate-500">Creators</span>
                                            <span class="text-sm font-semibold text-slate-900">{{ formatLargeNumber(item.item_count) }}</span>
                                        </div>
                                        <div class="flex flex-col">
                                            <span class="text-xs text-slate-500">Views</span>
                                            <span class="text-sm font-semibold text-slate-900">{{ formatLargeNumber(item.facets?.all?.all?.sum_views ?? 0) }}</span>
                                        </div>
                                        <div v-if="item.facets?.all?.all?.content_count" class="flex flex-col">
                                            <span class="text-xs text-slate-500">Content</span>
                                            <span class="text-sm font-semibold text-slate-900">{{ formatLargeNumber(item.facets.all.all.content_count) }}</span>
                                        </div>
                                    </div>

                                    <!-- Footer: Created info + hover action -->
                                    <div class="flex items-center justify-between">
                                        <div class="text-xs text-slate-500">
                                            <span>{{ $moment(item.generated_at).fromNow() }}</span>
                                            <span v-if="item?.operator"> by {{ item.operator.first_name }} {{ item.operator.last_name }}</span>
                                        </div>
                                        <div class="text-xs font-medium text-indigo-600 flex items-center gap-1">
                                            <span>View report</span>
                                            <AppIcon name="arrow-right" class="h-3 w-3" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="ml-2 mr-2 mt-4">
                            <DiscoveryPagination
                                :paging="reportHistoryPaging"
                                record-name="Reports"
                                :page-sizes="['10', '15', '20', '50', '100']"
                                @paging-changed="onHistoryPagingChange"
                            />
                        </div>
                    </div>

                    <NoResults
                        v-else
                        title="No reports found"
                        message="Try adjusting your filters or create a new report to get started."
                    />
                </div>
            </div>
        </template>

        <!-- Generating State: Shows when report is being generated in the background -->
        <template v-if="activeSubPage === 'generating'">
            <div class="flex flex-col items-center justify-center py-16">
                <div class="w-full max-w-md">
                    <!-- Animated icon -->
                    <div class="flex justify-center mb-6">
                        <div class="relative">
                            <div class="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center">
                                <AppIcon
                                    name="chart-bar"
                                    class="h-10 w-10 text-indigo-600"
                                />
                            </div>
                            <div class="absolute inset-0 h-20 w-20 animate-ping rounded-full bg-indigo-400 opacity-20"></div>
                        </div>
                    </div>

                    <!-- Title -->
                    <h2 class="text-xl font-semibold text-center text-slate-900 mb-2">
                        Generating Your Report
                    </h2>

                    <!-- Progress message -->
                    <p class="text-sm text-center text-slate-600 mb-6">
                        {{ progressMessage || 'Preparing your report...' }}
                    </p>

                    <!-- Progress bar -->
                    <div class="w-full mb-6">
                        <div class="flex items-center justify-between text-xs text-slate-500 mb-2">
                            <span>Progress</span>
                            <span>{{ reportProgress }}%</span>
                        </div>
                        <Progress :model-value="reportProgress" class="h-3" />
                    </div>

                    <!-- Info text -->
                    <div class="bg-slate-50 rounded-lg p-4 mb-6">
                        <div class="flex items-start gap-3">
                            <AppIcon
                                name="info-circle"
                                class="h-5 w-5 text-slate-400 mt-0.5"
                            />
                            <div class="text-sm text-slate-600">
                                <p>Large reports may take several minutes.</p>
                                <p>You can safely leave this page while we process your request.</p>
                            </div>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="flex justify-center gap-3">
                        <Button variant="outline" @click="onShowHistory">
                            <AppIcon name="arrow-left" class="h-3 w-3 mr-2" />
                            Back to Reports
                        </Button>
                        <Button variant="destructive" @click="cancelReportGeneration">
                            <AppIcon name="times" class="h-3 w-3 mr-2" />
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>
        </template>

        <template v-if="activeSubPage === 'view'">
            <template v-if="isLoadingReport">
                <div class="mb-4 flex flex-col gap-3">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2 text-slate-600">
                            <div class="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600"></div>
                            <span class="text-sm font-medium">{{ progressMessage || 'Loading report...' }}</span>
                        </div>
                    </div>
                </div>
                <SkeletonReportItem />
            </template>
            <template v-else>
                <div v-if="!report?.version" class="mb-4 rounded-sm border bg-amber-100 border-amber-200 px-4 py-3 text-sm text-amber-700">
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div class="flex items-center gap-3">
                            <svg
                                class="h-4 w-4 shrink-0 fill-current opacity-80 text-amber-600"
                                viewBox="0 0 16 16"
                            >
                                <path
                                    d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z"
                                />
                            </svg>
                            <p>This report was generated using an older format. Regenerate it to access the latest charts and analytics.</p>
                        </div>
                        <Button
                            size="sm"
                            class="shrink-0"
                            :disabled="insufficientQuota"
                            @click="onShowGenerateReport(true, true)"
                        >
                            <AppIcon name="sync" class="mr-1.5 h-3 w-3" />
                            Regenerate Report
                        </Button>
                    </div>
                </div>
                <div class="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:gap-4">
                    <div class="flex w-full flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <!-- Main tabs (Overview, Creators, Content) on the left -->
                        <Tabs v-model="activeTab" class="flex-1">
                            <TabsList class="inline-flex rounded-md bg-slate-200 p-1 shadow-sm hover:shadow-sm focus:shadow-sm w-full sm:w-auto">
                                <TabsTrigger
                                    class="px-3 py-2 font-semibold leading-tight capitalize sm:px-4 hover:text-foreground"
                                    value="overview"
                                >
                                    Overview
                                </TabsTrigger>
                                <TabsTrigger
                                    class="px-3 py-2 font-semibold leading-tight capitalize sm:px-4 hover:text-foreground"
                                    value="creators"
                                >
                                    Creators
                                </TabsTrigger>
                                <TabsTrigger
                                    class="px-3 py-2 font-semibold leading-tight capitalize sm:px-4 hover:text-foreground"
                                    value="content"
                                >
                                    Content
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>

                        <!-- Report split selector (for Overview tab pre-computed aggregations) -->
                        <div v-if="activeTab === 'overview'" class="w-full sm:w-auto sm:ml-4">
                            <SelectRoot v-model="splitSelectionValue" :disabled="!report?.facets">
                                <SelectTrigger class="h-10 w-full min-w-[220px] justify-between rounded-md border border-slate-200 bg-white px-3 text-left text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-[240px]">
                                    <SelectValue placeholder="Select split" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem :value="defaultSplitOption.value">
                                        {{ defaultSplitOption.label }}
                                    </SelectItem>
                                    <template v-for="group in splitDropdownGroups">
                                        <SelectGroup v-if="group.options.length" :key="group.label">
                                            <SelectLabel>{{ group.label }}</SelectLabel>
                                            <SelectItem
                                                v-for="option in group.options"
                                                :key="option.value"
                                                :value="option.value"
                                            >
                                                {{ option.label }}
                                            </SelectItem>
                                        </SelectGroup>
                                    </template>
                                </SelectContent>
                            </SelectRoot>
                        </div>
                    </div>
                </div>

                <Tabs v-model="activeTab">
                    <TabsContent value="overview">
                        <CreatorAggregationChart
                            ref="overviewComponent"
                            :key="`${report?._id}-${splitSelectionValue}`"
                            :sponsored-timeseries-data="sponsoredTimeseriesData"
                            :organic-timeseries-data="organicTimeseriesData"
                            :facets="report?.facets"
                            :aggregation-data="demographicFacets"
                            :summary="currentAggregates"
                            :is-loading="!report"
                            :is-loading-facets="isLoadingFacets"
                            :selected-facet-dimension="selectedFacetDimension"
                            :selected-facet-key="selectedFacetKey"
                            :selected-facet-label="selectedFacetLabel"
                            :creators="creators"
                            :sponsorship-enabled="isSponsorshipDetectionEnabled"
                            :report-id="report?._id"
                        />
                    </TabsContent>
                    <TabsContent value="creators">
                        <div>
                            <ReportTable
                                :key="`${report?._id}`"
                                ref="creatorsComponent"
                                :platform-id="reportPlatformIds.length === 1 ? reportPlatformIds[0] : null"
                                :platform-ids="reportPlatformIds"
                                :creators="creators"
                                :is-loading="isLoading"
                                :type="'platform'"
                                :paging="paging"
                                :available-platform-options="availablePlatformOptions"
                                :available-content-type-options="availableContentTypeOptions"
                                :available-language-options="availableLanguageOptions"
                                :available-country-options="availableCountryOptions"
                                :reportTitle="report?.title"
                                @paging-change="onPagingChange"
                                @export="exportReportItems"
                            >
                            </ReportTable>
                        </div>
                    </TabsContent>

                    <TabsContent value="content">
                        <div class="rounded bg-white px-2 py-3">
                            <ContentTable
                                :key="`${report?._id}`"
                                ref="contentComponent"
                                :report-id="report?._id"
                                :platform-id="reportPlatformIds.length === 1 ? reportPlatformIds[0] : null"
                                :platform-ids="reportPlatformIds"
                                :content-type="report?.options?.contentType"
                                :day-range="'day_30'"
                                :available-platform-options="availablePlatformOptions"
                                :available-content-type-options="availableContentTypeOptions"
                                :available-language-options="availableLanguageOptions"
                                :available-country-options="availableCountryOptions"
                                :report-start-date="report?.options?.startAt"
                                :report-end-date="report?.options?.endAt"
                                :reportTitle="report?.title"
                                @sponsorship-override-applied="onSponsorshipOverrideApplied"
                            />
                        </div>
                    </TabsContent>
                </Tabs>
            </template>
        </template>

        <template v-if="activeSubPage === 'error'">
            <div class="flex flex-col items-center justify-center py-16">
                <div class="w-full max-w-md">

                    <div class="flex justify-center mb-6">
                        <div class="relative">
                            <div class="h-20 w-20 rounded-full bg-rose-100 flex items-center justify-center">
                                <AppIcon
                                    name="triangle-exclamation"
                                    class="h-10 w-10 text-rose-500"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Title -->
                    <h2 class="text-xl font-semibold text-center text-slate-900 mb-2">
                        Error generating your report
                    </h2>

                    <!-- Info text -->
                    <div class="bg-slate-50 rounded-lg p-4 mb-6">
                        <div class="flex items-start gap-3">
                            <AppIcon
                                name="info-circle"
                                class="h-5 w-5 text-slate-400 mt-0.5"
                            />
                            <div class="text-sm text-slate-600">
                                <p>Unfortunately there was a problem generating your report.</p>
                                <p>Please try again, if the problem persists please <a class="text-indigo-500 hover:text-indigo-600 cursor-pointer" @click="onContactUs()">contact support</a> for assistance.</p>
                            </div>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="flex justify-center gap-3">
                        <Button variant="outline" @click="onShowHistory">
                            <AppIcon name="arrow-left" class="h-3 w-3 mr-2" />
                            Back to Reports
                        </Button>
                        <Button variant="outline" @click="onShowGenerateReport(true)">
                            <AppIcon name="copy" class="h-4 w-4 text-slate-500 mr-2" />
                            Retry report
                        </Button>
                        <Button variant="outline" @click="isDeleteOpen = true">
                            <AppIcon name="trash" class="h-4 w-4 mr-2 text-rose-600 focus:text-rose-400" />
                            <span class="text-rose-600 focus:text-rose-600  focus:text-rose-400">Delete report</span>
                        </Button>
                    </div>
                </div>
            </div>
        </template>

        <template v-if="activeSubPage === 'error-size'">
            <div class="flex flex-col items-center justify-center py-16">
                <div class="w-full max-w-md">

                    <div class="flex justify-center mb-6">
                        <div class="relative">
                            <div class="h-20 w-20 rounded-full bg-rose-100 flex items-center justify-center">
                                <AppIcon
                                    name="triangle-exclamation"
                                    class="h-10 w-10 text-rose-500"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Title -->
                    <h2 class="text-xl font-semibold text-center text-slate-900 mb-2">
                        Report too large
                    </h2>

                    <!-- Info text -->
                    <div class="bg-slate-50 rounded-lg p-4 mb-6">
                        <div class="flex items-start gap-3">
                            <AppIcon
                                name="info-circle"
                                class="h-5 w-5 text-slate-400 mt-0.5"
                            />
                            <div class="text-sm text-slate-600">
                                <p>Unfortunately the report you attempted to generate was too large.</p>
                                <p>Please try again with a more restricted date range.</p>
                            </div>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="flex justify-center gap-3">
                        <Button variant="outline" @click="onShowHistory">
                            <AppIcon name="arrow-left" class="h-3 w-3 mr-2" />
                            Back to Reports
                        </Button>
                        <Button variant="outline" @click="onShowGenerateReport(true)">
                            <AppIcon name="copy" class="h-4 w-4 text-slate-500 mr-2" />
                            Retry report
                        </Button>
                    </div>
                </div>
            </div>
        </template>
    </ContentLayout>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

.fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}
</style>
